import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import ResultStatus from "../classes/ResultStatus";
import SuccessMessage from "../classes/SuccessMessage";
import Popup from "./Popup";
import EventBus from "../utils/EventBus";
import MaterialIcons from "../icons/MaterialIcons";
import ServiceCategory from "../models/ServiceCategory";
import IconButton from "./IconButton";
import Categories from "../classes/Categories";
import Result from "../classes/Result";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import SearchComponent from "./SearchComponent";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import TextView from "./TextView";
import AddOrEditServiceCategoryView from "./AddOrEditServiceCategoryView";
import CollectionUtils from "../utils/CollectionUtils";
import BusinessUtils from "../classes/BusinessUtils";
import Divider from "./Divider";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _AddServiceCategoryButtonOnPressed = (
  d3eState: AdminCategoryManagementPageRefs
) => void;

export interface AdminCategoryManagementPageProps extends BaseUIProps {
  key?: string;
}
/// To store state data for AdminCategoryManagementPage
class AdminCategoryManagementPageRefs {
  public addServiceCategoryButton: AddServiceCategoryButtonState =
    new AddServiceCategoryButtonState();
}

interface AddServiceCategoryButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminCategoryManagementPageRefs;
  _onPressedAddServiceCategoryButtonHandler?: _AddServiceCategoryButtonOnPressed;
}

class AddServiceCategoryButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddServiceCategoryButtonWithState extends ObservableComponent<AddServiceCategoryButtonWithStateProps> {
  addServiceCategoryButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddServiceCategoryButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addServiceCategoryButton(): AddServiceCategoryButtonState {
    return this.props.d3eState.addServiceCategoryButton;
  }
  public get d3eState(): AdminCategoryManagementPageRefs {
    return this.props.d3eState;
  }
  public get _onPressedAddServiceCategoryButtonHandler(): _AddServiceCategoryButtonOnPressed {
    return this.props._onPressedAddServiceCategoryButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "addServiceCategoryButton",
      null,
      this.addServiceCategoryButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "addServiceCategoryButton",
        "addServiceCategoryButton.",
        "addServiceCategoryButton.hover",
      ],
      this.rebuild
    );
  }
  public addServiceCategoryButtonOnEnter(event): void {
    return this.addServiceCategoryButton.setHover(true);
  }
  public addServiceCategoryButtonOnExit(event): void {
    return this.addServiceCategoryButton.setHover(false);
  }
  public dispose(): void {
    this.addServiceCategoryButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.addServiceCategoryButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.addServiceCategoryButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.addServiceCategoryButton.disable,
        onPressed: () => {
          this._onPressedAddServiceCategoryButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "+ Add Service Category" }),
        onEnter: (event) => {
          this.addServiceCategoryButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addServiceCategoryButtonOnExit(event);
        },
      }),
      className: "x4ca",
    });
  }
}
function AddServiceCategoryButtonWithState(
  props: AddServiceCategoryButtonWithStateProps
) {
  return React.createElement(_AddServiceCategoryButtonWithState, props);
}

class _AdminCategoryManagementPageState extends ObservableComponent<AdminCategoryManagementPageProps> {
  d3eState: AdminCategoryManagementPageRefs =
    new AdminCategoryManagementPageRefs();
  categoriesList: Categories = null;
  categories: Array<ServiceCategory> = ListWrapper.widget(this, "categories");
  searchText: string = "";
  filterCategories: Array<ServiceCategory> = ListWrapper.widget(
    this,
    "filterCategories"
  );
  serviceCategory: ServiceCategory = null;
  createServiceCategoryPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminCategoryManagementPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.computeCategoriesList();

    this.on(["categoriesList", "categoriesList.items"], this.computeCategories);

    this.computeCategories();

    this.on(
      ["categories", "categories.name", "searchText"],
      this.computeFilterCategories
    );

    this.computeFilterCategories();

    this.on(
      [
        "filterCategories",
        "filterCategories.image",
        "filterCategories.inCall",
        "filterCategories.name",
        "filterCategories.outCall",
        "filterCategories.teleCall",
      ],
      this.rebuild
    );
  }
  public setCategoriesList(val: Categories): void {
    let isValChanged: boolean = this.categoriesList !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("categoriesList", this.categoriesList, val);

    MessageDispatch.get().dispose(this.categoriesList);

    this.categoriesList = val;

    this.fire("categoriesList", this);
  }
  public computeCategoriesList = async (): Promise<void> => {
    try {
      this.setCategoriesList(
        await Query.get().getCategories(
          UsageConstants.QUERY_GETCATEGORIES_ADMINCATEGORYMANAGEMENTPAGE_PROPERTIES_CATEGORIESLIST_COMPUTATION,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeCategoriesList : " + exception.toString()
      );

      this.setCategoriesList(null);
    }
  };
  public setCategories(val: Array<ServiceCategory>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.categories,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("categories", this.categories, val);

    this.categories.clear();

    this.categories.addAll(val);

    this.fire("categories", this);
  }
  public addToCategories(val: ServiceCategory, index: number = -1): void {
    if (index === -1) {
      if (!this.categories.contains(val)) this.categories.add(val);
    } else {
      this.categories.remove(this.categories.elementAt(index));

      this.categories.add(val);
    }

    this.fire("categories", this, val, true);

    this.updateObservable("categories", null, val);
  }
  public removeFromCategories(val: ServiceCategory): void {
    this.categories.remove(val);

    this.fire("categories", this, val, false);

    this.removeObservable("categories", val);
  }
  public computeCategories = (): void => {
    try {
      this.setCategories(
        Array.from(
          this.categoriesList.items.isNotEmpty ? this.categoriesList.items : []
        )
      );
    } catch (exception) {
      console.log(" exception in computeCategories : " + exception.toString());

      this.setCategories([]);
    }
  };
  public setSearchText(val: string): void {
    let isValChanged: boolean = this.searchText !== val;

    if (!isValChanged) {
      return;
    }

    this.searchText = val;

    this.fire("searchText", this);
  }
  public setFilterCategories(val: Array<ServiceCategory>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.filterCategories,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("filterCategories", this.filterCategories, val);

    this.filterCategories.clear();

    this.filterCategories.addAll(val);

    this.fire("filterCategories", this);
  }
  public addToFilterCategories(val: ServiceCategory, index: number = -1): void {
    if (index === -1) {
      if (!this.filterCategories.contains(val)) this.filterCategories.add(val);
    } else {
      this.filterCategories.remove(this.filterCategories.elementAt(index));

      this.filterCategories.add(val);
    }

    this.fire("filterCategories", this, val, true);

    this.updateObservable("filterCategories", null, val);
  }
  public removeFromFilterCategories(val: ServiceCategory): void {
    this.filterCategories.remove(val);

    this.fire("filterCategories", this, val, false);

    this.removeObservable("filterCategories", val);
  }
  public computeFilterCategories = (): void => {
    try {
      this.setFilterCategories(
        Array.from(
          this.categories.where((c) =>
            c.name.toLowerCase().contains(this.searchText.toLowerCase())
          )
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeFilterCategories : " + exception.toString()
      );

      this.setFilterCategories([]);
    }
  };
  public setServiceCategory(val: ServiceCategory): void {
    let isValChanged: boolean = this.serviceCategory !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("serviceCategory", this.serviceCategory, val);

    this.serviceCategory = val;

    this.fire("serviceCategory", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        TextView({
          data: "Category Management",
          style: new ui.TextStyle({
            fontSize: cStyle.tTextViewHeadlineTwoFontSizeOn,
            fontWeight: cStyle.tTextViewHeadlineTwoFontWeightOn,
          }),
          className: "xf3 hc",
          key: "0",
        }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.zero,
                  width: 300,
                  child: SearchComponent({
                    placeholder: "Search",
                    onChanged: (value) => {
                      this.searchHandler(value, this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x33 hc",
                }),
                AddServiceCategoryButtonWithState({
                  d3eState: this.d3eState,
                  _onPressedAddServiceCategoryButtonHandler:
                    this.onPressedAddServiceCategoryButtonHandler,
                  key: "1",
                }),
              ],
              className: "xba9 hc h",
              key: "0",
            }),
            Divider({ className: "x2b7 hc h", key: "1" }),
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          child: TextView({
                            data: "Category",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x1c6 hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Service Availability",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "xf65 hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Actions",
                            style: new ui.TextStyle({
                              fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewHeadlineFourFontWeightOn,
                            }),
                            className: "x306 hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                      ],
                      className: "x5b0 hc",
                      key: "0",
                    }),
                    this.filterCategories.expand((ctItem) => [
                      ui.TableRow({
                        children: [
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: ui.Row({
                              children: [
                                ctItem.image === null
                                  ? ui.ClipRRect({
                                      borderRadius: ui.BorderRadius.circular(4),
                                      child: ui.AssetImage({
                                        path: "images/Kitchen.jpg",
                                        width: 40,
                                        fit: ui.BoxFit.cover,
                                        height: 40,
                                        className: "x0d hc vc",
                                      }),
                                    })
                                  : ui.ClipRRect({
                                      borderRadius: ui.BorderRadius.circular(4),
                                      child: ui.NetworkImage({
                                        url: ctItem.image.downloadUrl,
                                        width: 40,
                                        fit: ui.BoxFit.cover,
                                        height: 40,
                                        className: "x00 hc vc",
                                      }),
                                    }),
                                TextView({
                                  data: ctItem.name,
                                  style: new ui.TextStyle({
                                    fontSize:
                                      cStyle.tTextViewHeadlineSixFontSizeOn,
                                    fontWeight:
                                      cStyle.tTextViewHeadlineSixFontWeightOn,
                                  }),
                                  className: "x3d",
                                  key: "1",
                                }),
                              ],
                              className: "x3bb hc h",
                            }),
                            className: "x62 hc h",
                            key: "0",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: TextView({
                              data: BusinessUtils.getCategeryServiceType(
                                ctItem
                              ),
                              className: "hc",
                            }),
                            className: "hc",
                            key: "1",
                          }),
                          ui.TableCell({
                            verticalAlignment:
                              ui.TableCellVerticalAlignment.middle,
                            child: ui.Row({
                              children: [
                                IconButton({
                                  icon: MaterialIcons.edit,
                                  color: cStyle.c4,
                                  hoverColor: cStyle.c1,
                                  onPressed: () => {
                                    this.addFileHandler(ctItem, this.d3eState);
                                  },
                                  key: "0",
                                }),
                                IconButton({
                                  icon: MaterialIcons.delete,
                                  color: cStyle.c4,
                                  hoverColor: cStyle.c1,
                                  onPressed: () => {
                                    this.deleteFileHandler(
                                      ctItem,
                                      this.d3eState
                                    );
                                  },
                                  key: "1",
                                }),
                              ],
                              className: "x9e9 hc h",
                            }),
                            className: "x5c hc h",
                            key: "2",
                          }),
                        ],
                        className: "x34 hc h",
                        key: ctItem?.ident,
                      }),
                    ]),
                  ],
                  className: "x958 hc h",
                  key: "0",
                }),
              ],
              className: "x9e8 hc h",
              key: "2",
            }),
          ],
          className: "xf5 hc h",
          key: "1",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminCategoryManagementPage x9b hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onPressedAddServiceCategoryButtonHandler = (
    d3eState: AdminCategoryManagementPageRefs
  ): void => {
    this.setServiceCategory(new ServiceCategory());

    this.showCreateServiceCategory();
  };
  public searchHandler = (
    value: string,
    d3eState: AdminCategoryManagementPageRefs
  ): void => {
    this.setSearchText(value);
  };
  public deleteFileHandler = async (
    ctItem: ServiceCategory,
    d3eState: AdminCategoryManagementPageRefs
  ): Promise<void> => {
    let result: Result<ServiceCategory> = await ctItem.delete();

    if (result.status === ResultStatus.Success) {
      EventBus.get().fire(
        new SuccessMessage({ message: "Deleted Successfully" })
      );
    }
  };
  public addFileHandler = (
    ctItem: ServiceCategory,
    d3eState: AdminCategoryManagementPageRefs
  ): void => {
    this.setServiceCategory(ctItem);

    this.showCreateServiceCategory();
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.categoriesList);

    this.createServiceCategoryPopup?.dispose();

    super.dispose();
  }
  public showCreateServiceCategory(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.createServiceCategoryPopup?.dispose();

    this.createServiceCategoryPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        height: 550,
        width: 400,
        child: AddOrEditServiceCategoryView({
          serviceCategory: this.serviceCategory,
        }),
        className: "xbb hc vc",
      }),
    });

    this.createServiceCategoryPopup.showPopup(this.context);
  }
  public hideCreateServiceCategory(): void {
    this.createServiceCategoryPopup?.dispose();
  }
  public get addServiceCategoryButton() {
    return this.d3eState.addServiceCategoryButton;
  }
}
export default function AdminCategoryManagementPage(
  props: AdminCategoryManagementPageProps
) {
  return React.createElement(_AdminCategoryManagementPageState, props);
}
